import {useState, useEffect, useRef} from 'react'
import {KTIcon} from '../../../../../_metronic/helpers'
import {StepProps} from '../IAppModels'
import {IPatientData} from '../../../interface/IPatientData'
import {useAuth} from '../../../../../app/modules/auth'
import CustomCalendar from '../../../../../_metronic/layout/calendar'

interface IMemberData {
  status: string
  corporateCode: string
  corporateName: string
  insurerCode: string
  insurerName: string
  memberId: string
  memberInsuranceNumber: string
  cardNumber: string
  nik: string | null
  memberName: string
  memberStatus: boolean
  memberType: string
  dateOfBirth: string
  gender: string
  maritalStatus: string
  isSuspended: boolean
  isVip: number
  isAso: boolean
  isIndividual: boolean
  isGroup: boolean
  productCode: string
  policyNo: string
  policyStart: string
  policyEnd: string
  joinDate: string
  remark: string
  type: string
  effectiveDate: string
  expiryDate: string
  benefits: any[]
}

const Step1 = ({
  data,
  updateData,
  hasError,
  handlePatientDataFetchSuccess,
  handlePatientDataFetchError,
  handleClaimValidationSuccess,
}: StepProps) => {
  const user = useAuth()
  const [patientData, setPatientData] = useState<IPatientData | null>(null)
  const [membersList, setMembersList] = useState<IMemberData[]>([])
  const [selectedMember, setSelectedMember] = useState<IMemberData | null>(null)
  const [loading, setLoading] = useState(false)
  const [claimValidationLoading, setClaimValidationLoading] = useState(false)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [errorMessageClaim, setErrorMessageClaim] = useState<string | null>(null)
  const [cardNo, setCardNo] = useState<string>('')
  const [inputCardNo, setInputCardNo] = useState<string>(data.appBasic.identification)
  const [searchType, setSearchType] = useState<string>('Nomor Kartu')
  const [isDropdownOpen, setIsDropdownOpen] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)
  const {auth, currentUser} = useAuth()

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [])

  // Format date to Indonesian format (e.g., "4 November 1973")
  const formatDateToIndonesian = (dateString: string) => {
    const months = [
      'Januari',
      'Februari',
      'Maret',
      'April',
      'Mei',
      'Juni',
      'Juli',
      'Agustus',
      'September',
      'Oktober',
      'November',
      'Desember',
    ]

    const date = new Date(dateString)
    const day = date.getDate()
    const month = months[date.getMonth()]
    const year = date.getFullYear()

    return `${day} ${month} ${year}`
  }

  const fetchValidateClaim = async (cardNumber?: string) => {
    const cardToValidate = cardNumber || inputCardNo
    setClaimValidationLoading(true)
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_CLAIM_URL}/api/Claims/Provider/CreateClaimValidation?CardNumber=${cardToValidate}`,
        {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${auth?.token}` || `Bearer ${user.currentUser?.token}`,
          },
        }
      )

      if (!response.ok && response.status !== 200) {
        handleClaimValidationSuccess && handleClaimValidationSuccess(false)
        setErrorMessageClaim(`Member memiliki klaim aktif*. Silakan hubungi Helpline AXA Financial Indonesia di 021-30019292 untuk informasi lebih lanjut.
          (*Klaim aktif adalah klaim non tunai yang belum selesai)`)
        return // Exit the function early
      }

      if (response.ok) {
        handleClaimValidationSuccess && handleClaimValidationSuccess(true)
        setErrorMessageClaim(null) // Clear error message on successful validation
      }
    } catch (error) {
      console.log('Error fetching patient data:', error)
      handleClaimValidationSuccess && handleClaimValidationSuccess(false)
      // Check if the error is a SyntaxError
      if (error instanceof SyntaxError) {
        // Handle specific syntax errors
        if (error.message.includes("Unexpected token 'T'")) {
          setErrorMessageClaim(`Member memiliki klaim aktif*. Silakan hubungi Helpline AXA Financial Indonesia di 021-30019292 untuk informasi lebih lanjut.
(*Klaim aktif adalah klaim non tunai yang belum selesai)`)
        }
      } else {
        setErrorMessageClaim('An unexpected error occurred')
      }
    } finally {
      setClaimValidationLoading(false)
    }
  }

  const fetchPatientDataByPolicy = async () => {
    const dob = data.appBasic.birthdate
    const providerCode = user.currentUser?.providerCode

    if (!inputCardNo || !dob) {
      alert('Please enter both Policy Number and Date of Birth')
      return
    }

    setErrorMessage(null)
    setErrorMessageClaim(null)
    setPatientData(null)
    setMembersList([])
    setSelectedMember(null)
    setLoading(true)
    setCardNo(inputCardNo)

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_USER_URL}/api/Data/Provider/GetMemberByPolisNumber?ProviderCode=${providerCode}&PolicyNo=${inputCardNo}&Dob=${dob}`,
        {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${auth?.token}` || `Bearer ${user.currentUser?.token}`,
          },
        }
      )

      // Check if the response is not ok (status code not in the range 200-299)
      if (!response.ok) {
        const errorResult = await response.json()
        console.log('ERROR MESS : ', errorResult.message)
        // Check for specific error messages
        if (
          errorResult.message === 'Member not found' ||
          errorResult.message === 'MemberNotFound'
        ) {
          setErrorMessage(
            'Data member tidak ditemukan. Harap pastikan nomor polis dan tanggal lahir sudah diisi dengan benar.'
          )
        } else if (
          errorResult.message === 'StatusInactive' ||
          errorResult.message.includes('Unexpected token StatusInactive')
        ) {
          setErrorMessage(
            `Status member tidak Aktif. Silakan hubungi Helpline AXA Financial Indonesia di 021-30019292 untuk informasi lebih lanjut.`
          )
        } else if (errorResult.message === 'ActiveClaim') {
          setErrorMessage(
            `Member memiliki klaim aktif. Silakan hubungi Helpline AXA Financial Indonesia di 021-30019292 untuk informasi lebih lanjut.
(*Klaim aktif adalah klaim non tunai yang belum selesai)`
          )
        } else if (
          errorResult.message ===
            'Data Pasien Ditemukan, Namun tidak dapat berobat di Rumah Sakit' ||
          errorResult.message === 'Exclusion'
        ) {
          setErrorMessage(
            `Pendaftaran tidak bisa diproses karena tidak termasuk provider yang dijaminkan untuk peserta ini. Silakan hubungi Helpline AXA Financial Indonesia di 021-30019292 untuk informasi lebih lanjut.`
          )
        } else {
          setErrorMessage(errorResult.message || 'An error occurred while fetching data')
        }
        handlePatientDataFetchError && handlePatientDataFetchError()
        return // Exit the function early
      }

      const result = await response.json()

      if (result.isSuccess && result.data && result.data.length > 0) {
        // Check all statuses in the response
        const allStatuses = result.data.map((member: IMemberData) => member.status)
        const uniqueStatuses = Array.from(new Set(allStatuses))

        // Check if all members have StatusInactive
        if (uniqueStatuses.length === 1 && uniqueStatuses[0] === 'StatusInactive') {
          setErrorMessage(
            `Status member tidak Aktif. Silakan hubungi Helpline AXA Financial Indonesia di 021-30019292 untuk informasi lebih lanjut.`
          )
          handlePatientDataFetchError && handlePatientDataFetchError()
          return
        }

        // Filter only active members
        const activeMembers = result.data.filter(
          (member: IMemberData) => member.status === 'Active'
        )

        if (activeMembers.length === 0) {
          setErrorMessage(
            `Status member tidak Aktif. Silakan hubungi Helpline AXA Financial Indonesia di 021-30019292 untuk informasi lebih lanjut.`
          )
          handlePatientDataFetchError && handlePatientDataFetchError()
          return
        }

        setMembersList(activeMembers)

        // Auto-select if only one member
        if (activeMembers.length === 1) {
          handleMemberSelect(activeMembers[0])
        } else {
          // Reset patient data state since user needs to select a member
          handlePatientDataFetchError && handlePatientDataFetchError()
        }
      } else {
        console.log('MATANE : ', result)
        setErrorMessage('Data tidak ditemukan')
        handlePatientDataFetchError && handlePatientDataFetchError()
      }
    } catch (error) {
      console.log('Error fetching patient data:', error)
      // Check if the error is a SyntaxError
      if (error instanceof SyntaxError) {
        // Handle specific syntax errors
        if (error.message.includes("Unexpected token 'D'")) {
          setErrorMessage(
            `Pendaftaran tidak bisa diproses karena tidak termasuk provider yang dijaminkan untuk peserta ini. Silakan hubungi Helpline AXA Financial Indonesia di 021-30019292 untuk informasi lebih lanjut.`
          )
        } else if (error.message.includes("Unexpected token 'U'")) {
          setErrorMessage(
            `Status member tidak Aktif. Silakan hubungi Helpline AXA Financial Indonesia di 021-30019292 untuk informasi lebih lanjut.`
          )
        } else if (
          error.message.includes("Unexpected token 'S'") ||
          error.message.includes('StatusInactive')
        ) {
          setErrorMessage(
            `Status member tidak Aktif. Silakan hubungi Helpline AXA Financial Indonesia di 021-30019292 untuk informasi lebih lanjut.`
          )
        } else if (
          error.message.includes("Unexpected token 'E'") ||
          error.message.includes('Exclusion')
        ) {
          setErrorMessage(
            `Pendaftaran tidak bisa diproses karena tidak termasuk provider yang dijaminkan untuk peserta ini. Silakan hubungi Helpline AXA Financial Indonesia di 021-30019292 untuk informasi lebih lanjut.`
          )
        } else if (
          error.message.includes("Unexpected token 'M'") ||
          error.message.includes('Membership')
        ) {
          setErrorMessage(
            'Data member tidak ditemukan. Harap pastikan nomor polis dan tanggal lahir sudah diisi dengan benar.'
          )
        } else {
          setErrorMessage('Member Not Found!')
        }
      } else {
        setErrorMessage('An unexpected error occurred')
      }
      handlePatientDataFetchError && handlePatientDataFetchError()
    } finally {
      setLoading(false)
    }
  }

  const fetchPatientData = async () => {
    const dob = data.appBasic.birthdate
    const providerCode = user.currentUser?.providerCode

    if (!inputCardNo || !dob) {
      alert('Please enter both Card Number and Date of Birth')
      return
    }

    setErrorMessage(null)
    setErrorMessageClaim(null)
    setPatientData(null)
    setMembersList([])
    setSelectedMember(null)
    setLoading(true)
    setCardNo(inputCardNo)

    const base_url =
      process.env.REACT_APP_API_USER_URL || 'http://id-afi-api-staging.healthmetrics.com/users'

    try {
      const response = await fetch(
        `${base_url}/api/Data/Provider/GetMemberByCard?ProviderCode=${providerCode}&CardNo=${inputCardNo}&Dob=${dob}`,
        // `${process.env.REACT_APP_API_USER_URL}/api/Data/Provider/GetMemberByCard?ProviderCode=${providerCode}&CardNo=${inputCardNo}&Dob=${dob}`,
        {
          headers: {
            'Content-Type': 'application/jsson',
            Authorization: `Bearer ${auth?.token}` || `Bearer ${user.currentUser?.token}`,
          },
        }
      )

      // Check if the response is not ok (status code not in the range 200-299)
      if (!response.ok) {
        const errorResult = await response.json()
        console.log('ERROR MESS : ', errorResult.message)
        // Check for specific error messages
        if (
          errorResult.message === 'Member not found' ||
          errorResult.message === 'MemberNotFound'
        ) {
          setErrorMessage(
            'Data member tidak ditemukan. Harap pastikan nomor kartu dan tanggal lahir sudah diisi dengan benar.'
          )
        } else if (
          errorResult.message === 'StatusInactive' ||
          errorResult.message.includes('Unexpected token StatusInactive')
        ) {
          setErrorMessage(
            `Status member tidak Aktif. Silakan hubungi Helpline AXA Financial Indonesia di 021-30019292 untuk informasi lebih lanjut.`
          )
        } else if (errorResult.message === 'ActiveClaim') {
          setErrorMessage(
            `Member memiliki klaim aktif. Silakan hubungi Helpline AXA Financial Indonesia di 021-30019292 untuk informasi lebih lanjut.
(*Klaim aktif adalah klaim non tunai yang belum selesai)`
          )
        } else if (
          errorResult.message ===
            'Data Pasien Ditemukan, Namun tidak dapat berobat di Rumah Sakit' ||
          errorResult.message === 'Exclusion'
        ) {
          setErrorMessage(
            `Pendaftaran tidak bisa diproses karena tidak termasuk provider yang dijaminkan untuk peserta ini. Silakan hubungi Helpline AXA Financial Indonesia di 021-30019292 untuk informasi lebih lanjut.`
          )
        } else {
          setErrorMessage(errorResult.message || 'An error occurred while fetching data')
        }
        handlePatientDataFetchError && handlePatientDataFetchError()
        return // Exit the function early
      }

      const result = await response.json()

      if (result.isSuccess) {
        fetchValidateClaim()
        setPatientData(result.data)
        console.log(result.data)
        updateData({
          appBasic: {
            ...data.appBasic,
            memberData: result.data,
          },
        })
        handlePatientDataFetchSuccess && handlePatientDataFetchSuccess(result.data)
      } else {
        console.log('MATANE : ', result)
        setErrorMessage('Data tidak ditemukan')
        handlePatientDataFetchError && handlePatientDataFetchError()
      }
    } catch (error) {
      console.log('Error fetching patient data:', error)
      // Check if the error is a SyntaxError
      if (error instanceof SyntaxError) {
        // Handle specific syntax errors
        if (error.message.includes("Unexpected token 'D'")) {
          setErrorMessage(
            `Pendaftaran tidak bisa diproses karena tidak termasuk provider yang dijaminkan untuk peserta ini. Silakan hubungi Helpline AXA Financial Indonesia di 021-30019292 untuk informasi lebih lanjut.`
          )
        } else if (error.message.includes("Unexpected token 'U'")) {
          setErrorMessage(
            `Status member tidak Aktif. Silakan hubungi Helpline AXA Financial Indonesia di 021-30019292 untuk informasi lebih lanjut.`
          )
        } else if (
          error.message.includes("Unexpected token 'S'") ||
          error.message.includes('StatusInactive')
        ) {
          setErrorMessage(
            `Status member tidak Aktif. Silakan hubungi Helpline AXA Financial Indonesia di 021-30019292 untuk informasi lebih lanjut.`
          )
        } else if (
          error.message.includes("Unexpected token 'E'") ||
          error.message.includes('Exclusion')
        ) {
          setErrorMessage(
            `Pendaftaran tidak bisa diproses karena tidak termasuk provider yang dijaminkan untuk peserta ini. Silakan hubungi Helpline AXA Financial Indonesia di 021-30019292   untuk informasi lebih lanjut.`
          )
        } else if (
          error.message.includes("Unexpected token 'M'") ||
          error.message.includes('Membership')
        ) {
          setErrorMessage(
            'Data member tidak ditemukan. Harap pastikan nomor kartu dan tanggal lahir sudah diisi dengan benar.'
          )
        } else {
          setErrorMessage('Member Not Found!')
        }
      } else {
        setErrorMessage('An unexpected error occurred')
      }
      handlePatientDataFetchError && handlePatientDataFetchError()
    } finally {
      setLoading(false)
    }
  }

  const handleSearchPatient = () => {
    if (searchType === 'Nomor Kartu') {
      fetchPatientData()
    } else {
      fetchPatientDataByPolicy()
    }
  }

  const handleMemberSelect = (member: IMemberData) => {
    setSelectedMember(member)
    // Convert IMemberData to IPatientData format
    const convertedPatientData: IPatientData = {
      productCode: member.productCode,
      corporateCode: member.corporateCode,
      corporateName: member.corporateName,
      insurerCode: member.insurerCode,
      insurerName: member.insurerName,
      memberId: member.memberId,
      memberInsuranceNumber: member.memberInsuranceNumber,
      cardNumber: member.cardNumber,
      nik: member.nik || '',
      memberName: member.memberName,
      memberStatus: member.memberStatus,
      memberType: member.memberType,
      dateOfBirth: member.dateOfBirth,
      gender: member.gender,
      maritalStatus: member.maritalStatus,
      isSuspended: member.isSuspended,
      isVip: member.isVip,
      isAso: member.isAso,
      isIndividual: member.isIndividual,
      planCode: '',
      planName: '',
      policyNo: member.policyNo,
      policyStart: member.policyStart,
      policyEnd: member.policyEnd,
      joinDate: member.joinDate,
      remark: member.remark,
      type: member.type,
      benefits: member.benefits,
    }

    setPatientData(convertedPatientData)
    console.log(convertedPatientData)
    updateData({
      appBasic: {
        ...data.appBasic,
        memberData: convertedPatientData,
      },
    })
    handlePatientDataFetchSuccess && handlePatientDataFetchSuccess(convertedPatientData)

    // Call claim validation for selected member
    fetchValidateClaim(member.cardNumber)
  }

  return (
    <div className='current' data-kt-stepper-element='content'>
      <div className='w-100'>
        {/* Identification Input */}
        <div className='fv-row mb-10'>
          <label className='d-flex align-items-center fs-6 fw-semibold mb-2'>
            <span>Identifikasi</span>
          </label>
          <div className='d-flex align-items-center'>
            <div
              ref={dropdownRef}
              className='custom-select-wrapper'
              style={{width: '30%', position: 'relative'}}
            >
              <div
                className='custom-select-trigger'
                onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                style={{
                  fontSize: '12px',
                  background: '#fff',
                  color: '#212529',
                  border: '1px solid #ced4da',
                  borderTopLeftRadius: '8px',
                  borderBottomLeftRadius: '8px',
                  borderTopRightRadius: 0,
                  borderBottomRightRadius: 0,
                  height: '45px',
                  paddingLeft: '12px',
                  paddingRight: '35px',
                  display: 'flex',
                  alignItems: 'center',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease-in-out',
                  backgroundImage: `url("data:image/svg+xml;charset=utf-8,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none'%3E%3Cpath d='M7 10L12 15L17 10' stroke='%23059BB6' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E")`,
                  backgroundRepeat: 'no-repeat',
                  backgroundPosition: 'right 12px center',
                  backgroundSize: '18px',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = '#059BB6'
                  e.currentTarget.style.boxShadow = '0 2px 4px rgba(5, 155, 182, 0.1)'
                  e.currentTarget.style.backgroundImage = `url("data:image/svg+xml;charset=utf-8,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none'%3E%3Cpath d='M7 10L12 15L17 10' stroke='%23047a93' stroke-width='2.5' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E")`
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = '#ced4da'
                  e.currentTarget.style.boxShadow = 'none'
                  e.currentTarget.style.backgroundImage = `url("data:image/svg+xml;charset=utf-8,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none'%3E%3Cpath d='M7 10L12 15L17 10' stroke='%23059BB6' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E")`
                }}
              >
                {searchType}
              </div>

              {isDropdownOpen && (
                <div
                  className='custom-select-options'
                  style={{
                    position: 'absolute',
                    top: '100%',
                    left: 0,
                    right: 0,
                    background: '#fff',
                    border: '1px solid #059BB6',
                    borderTop: 'none',
                    borderRadius: '0 0 6px 6px',
                    boxShadow: '0 4px 12px rgba(5, 155, 182, 0.15)',
                    zIndex: 1000,
                    overflow: 'hidden',
                  }}
                >
                  <div
                    className='custom-option'
                    onClick={() => {
                      setSearchType('Nomor Kartu')
                      setIsDropdownOpen(false)
                      setPatientData(null)
                      setMembersList([])
                      setSelectedMember(null)
                      setErrorMessage(null)
                      setErrorMessageClaim(null)
                      handlePatientDataFetchError && handlePatientDataFetchError()
                    }}
                    style={{
                      padding: '12px 16px',
                      fontSize: '12px',
                      color: searchType === 'Nomor Kartu' ? '#059BB6' : '#212529',
                      backgroundColor: searchType === 'Nomor Kartu' ? '#f8fffe' : '#fff',
                      cursor: 'pointer',
                      borderBottom: '1px solid #f1f3f4',
                      transition: 'all 0.2s ease-in-out',
                      fontWeight: searchType === 'Nomor Kartu' ? '600' : '400',
                    }}
                    onMouseEnter={(e) => {
                      if (searchType !== 'Nomor Kartu') {
                        e.currentTarget.style.backgroundColor = '#f8f9fa'
                        e.currentTarget.style.color = '#059BB6'
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (searchType !== 'Nomor Kartu') {
                        e.currentTarget.style.backgroundColor = '#fff'
                        e.currentTarget.style.color = '#212529'
                      }
                    }}
                  >
                    <div style={{display: 'flex', alignItems: 'center'}}>
                      <KTIcon iconName='credit-cart' className='me-2 text-primary' />
                      Nomor Kartu
                    </div>
                  </div>

                  <div
                    className='custom-option'
                    onClick={() => {
                      setSearchType('Nomor Polis')
                      setIsDropdownOpen(false)
                      setPatientData(null)
                      setMembersList([])
                      setSelectedMember(null)
                      setErrorMessage(null)
                      setErrorMessageClaim(null)
                      handlePatientDataFetchError && handlePatientDataFetchError()
                    }}
                    style={{
                      padding: '12px 16px',
                      fontSize: '12px',
                      color: searchType === 'Nomor Polis' ? '#059BB6' : '#212529',
                      backgroundColor: searchType === 'Nomor Polis' ? '#f8fffe' : '#fff',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease-in-out',
                      fontWeight: searchType === 'Nomor Polis' ? '600' : '400',
                    }}
                    onMouseEnter={(e) => {
                      if (searchType !== 'Nomor Polis') {
                        e.currentTarget.style.backgroundColor = '#f8f9fa'
                        e.currentTarget.style.color = '#059BB6'
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (searchType !== 'Nomor Polis') {
                        e.currentTarget.style.backgroundColor = '#fff'
                        e.currentTarget.style.color = '#212529'
                      }
                    }}
                  >
                    <div style={{display: 'flex', alignItems: 'center'}}>
                      <KTIcon iconName='document' className='me-2 text-primary' />
                      Nomor Polis
                    </div>
                  </div>
                </div>
              )}
            </div>

            <input
              type='text'
              className='form-control form-control-lg'
              style={{
                width: '70%',
                borderTopLeftRadius: 0,
                borderBottomLeftRadius: 0,
              }}
              placeholder={
                searchType === 'Nomor Kartu' ? 'Masukkan Nomor Kartu' : 'Masukkan Nomor Polis'
              }
              value={inputCardNo}
              onChange={(e) => setInputCardNo(e.target.value.trim())}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  handleSearchPatient()
                }
              }}
            />
          </div>
        </div>

        {/* Birthdate Input */}
        <CustomCalendar
          value={data.appBasic.birthdate}
          onChange={(date) =>
            updateData({
              appBasic: {
                ...data.appBasic,
                birthdate: date,
              },
            })
          }
          label='Tanggal Lahir'
          placeholder='Pilih Tanggal Lahir'
          required={false}
          maxDate={new Date()}
          className='mb-10'
        />

        {/* Search Button */}
        <button
          className='btn'
          type='button'
          style={{
            width: '100%',
            backgroundColor: '#059BB6',
            color: '#fff',
          }}
          onClick={handleSearchPatient}
        >
          {loading ? 'Loading...' : 'Cari Pasien'}
        </button>

        {/* Members List (for Policy Number search) */}
        {membersList.length > 0 && (
          <div className='mt-4'>
            <h6 className='mb-3'>Pilih Member:</h6>
            {membersList.map((member, index) => (
              <div
                key={member.memberId}
                className={`mb-3 p-3 border rounded cursor-pointer ${
                  selectedMember?.memberId === member.memberId
                    ? 'border-primary bg-light-primary'
                    : 'border-secondary'
                }`}
                style={{cursor: 'pointer'}}
                onClick={() => handleMemberSelect(member)}
              >
                <div className='d-flex justify-content-between align-items-center'>
                  <div>
                    <div className='fw-bold text-dark'>{member.memberName}</div>
                    <div className='text-muted small'>
                      DOB: {formatDateToIndonesian(member.dateOfBirth)}
                    </div>
                    <div className='text-muted small'>Nomor Kartu: {member.cardNumber}</div>
                    <div className='text-muted small'>Nomor Polis: {member.policyNo}</div>
                  </div>
                  <div>
                    {selectedMember?.memberId === member.memberId && (
                      <KTIcon iconName='check-circle' className='text-success fs-2' />
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Error Message Display */}
        {errorMessage && (
          <div
            className='mt-4'
            style={{
              border: '1px solid #F15E60',
              borderRadius: '6px',
              padding: '10px',
              backgroundColor: '#FFFFFF',
            }}
          >
            <div
              className='d-flex align-items-center'
              style={{color: '#F15E60', fontWeight: 'bold'}}
            >
              <KTIcon iconName='cross-circle' className='me-2 custom-red large-icon' />
              Pasien
            </div>
            <hr style={{border: '1px solid #F15E60', margin: '10px 0'}} />
            <div style={{color: '#333'}}>
              <span style={{color: '#b2b7bf'}}>{cardNo}</span> {errorMessage}
            </div>
          </div>
        )}

        {/* Success Message Display for Card Number search */}
        {patientData && membersList.length === 0 && (
          <div
            className='mt-4'
            style={{
              border: '1px solid #E0E0E0',
              borderRadius: '6px',
              padding: '10px',
              backgroundColor: '#FFFFFF',
            }}
          >
            <div
              className='d-flex align-items-center justify-content-between'
              style={{color: '#059BB6', fontWeight: 'bold'}}
            >
              <div className='d-flex align-items-center'>
                <KTIcon iconName='check-circle' className='me-2 custom-green large-icon' />
                Pasien
              </div>
              {claimValidationLoading && (
                <div className='d-flex align-items-center'>
                  <span
                    className='spinner-border spinner-border-sm me-2'
                    role='status'
                    aria-hidden='true'
                  ></span>
                  <span className='text-muted small'>Validating...</span>
                </div>
              )}
            </div>
            <hr style={{border: '1px solid #059BB6', margin: '10px 0'}} />
            <div className='mt-2'>
              <table className='table'>
                <tbody>
                  <tr>
                    <td className='text-muted'>Nama</td>
                    <td>{patientData.memberName || '-'}</td>
                  </tr>
                  <tr>
                    <td className='text-muted'>Jenis Kelamin</td>
                    <td>{patientData.gender === 'M' ? 'Laki-laki' : 'Perempuan'}</td>
                  </tr>
                  <tr>
                    <td className='text-muted'>Customer</td>
                    <td>
                      {patientData?.isAso ? patientData?.corporateName : patientData.insurerName}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Success Message Display for selected member from Policy search */}
        {selectedMember && membersList.length > 0 && (
          <div
            className='mt-4'
            style={{
              border: '1px solid #E0E0E0',
              borderRadius: '6px',
              padding: '10px',
              backgroundColor: '#FFFFFF',
            }}
          >
            <div
              className='d-flex align-items-center justify-content-between'
              style={{color: '#059BB6', fontWeight: 'bold'}}
            >
              <div className='d-flex align-items-center'>
                <KTIcon iconName='check-circle' className='me-2 custom-green large-icon' />
                Member Terpilih
              </div>
              {claimValidationLoading && (
                <div className='d-flex align-items-center'>
                  <span
                    className='spinner-border spinner-border-sm me-2'
                    role='status'
                    aria-hidden='true'
                  ></span>
                  <span className='text-muted small'>Validating...</span>
                </div>
              )}
            </div>
            <hr style={{border: '1px solid #059BB6', margin: '10px 0'}} />
            <div className='mt-2'>
              <table className='table'>
                <tbody>
                  <tr>
                    <td className='text-muted'>Nama</td>
                    <td>{selectedMember.memberName || '-'}</td>
                  </tr>
                  <tr>
                    <td className='text-muted'>Jenis Kelamin</td>
                    <td>{selectedMember.gender === 'M' ? 'Laki-laki' : 'Perempuan'}</td>
                  </tr>
                  <tr>
                    <td className='text-muted'>Customer</td>
                    <td>
                      {selectedMember?.isAso
                        ? selectedMember?.corporateName
                        : selectedMember.insurerName}
                    </td>
                  </tr>
                  <tr>
                    <td className='text-muted'>Nomor Kartu</td>
                    <td>{selectedMember.cardNumber}</td>
                  </tr>
                  <tr>
                    <td className='text-muted'>Nomor Polis</td>
                    <td>{selectedMember.policyNo}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Error Message Claim Display */}
        {(errorMessageClaim || claimValidationLoading) && (
          <div
            className='mt-4'
            style={{
              border: `1px solid ${claimValidationLoading ? '#059BB6' : '#F15E60'}`,
              borderRadius: '6px',
              padding: '10px',
              backgroundColor: '#FFFFFF',
            }}
          >
            <div
              className='d-flex align-items-center justify-content-between'
              style={{color: claimValidationLoading ? '#059BB6' : '#F15E60', fontWeight: 'bold'}}
            >
              <div className='d-flex align-items-center'>
                {claimValidationLoading ? (
                  <>
                    <span
                      className='spinner-border spinner-border-sm me-2'
                      role='status'
                      aria-hidden='true'
                    ></span>
                    Validating Claim...
                  </>
                ) : (
                  <>
                    <KTIcon iconName='cross-circle' className='me-2 custom-red large-icon' />
                    Pasien Memiliki Klaim Aktif
                  </>
                )}
              </div>
            </div>
            <hr
              style={{
                border: `1px solid ${claimValidationLoading ? '#059BB6' : '#F15E60'}`,
                margin: '10px 0',
              }}
            />
            {claimValidationLoading ? (
              <div style={{color: '#6c757d'}}>
                <span style={{color: '#b2b7bf'}}>{selectedMember?.cardNumber || cardNo}</span>
                <br />
                <p style={{color: '#b2b7bf', marginBottom: '0px'}}>
                  Memvalidasi status klaim member...
                </p>
                <p style={{color: '#b2b7bf', marginBottom: '0px'}}>Mohon tunggu sebentar.</p>
              </div>
            ) : (
              <div style={{color: '#333'}}>
                <span style={{color: '#b2b7bf'}}>{selectedMember?.cardNumber || cardNo}</span>
                <br />
                <p style={{color: '#b2b7bf', marginBottom: '0px'}}>Member memiliki klaim aktif*</p>
                <p style={{color: '#b2b7bf', marginBottom: '0px'}}>
                  Mohon hubungi telepon bebas pulsa AXA Financial Indonesia di 021-30019292 untuk respons
                  cepat atau informasi lebih lanjut.
                </p>
                <p style={{color: '#b2b7bf', marginBottom: '0px'}}>
                  Klaim aktif adalah klaim non tunai yang belum selesai
                </p>
              </div>
            )}
          </div>
        )}
      </div>
      <style>{`
        .custom-green {
          color: #04B440;
        }
        .custom-red {
          color: #F15E60;
        }
        .large-icon {
          font-size: 30px;
        }
        .bg-light-primary {
          background-color: rgba(5, 155, 182, 0.1) !important;
        }
        .border-primary {
          border-color: #059BB6 !important;
        }
        .custom-dropdown::-ms-expand {
          display: none;
        }
        .custom-dropdown {
          transition: all 0.2s ease-in-out;
        }
        .custom-dropdown:hover {
          border-color: #059BB6;
          background-image: url("data:image/svg+xml;charset=utf-8,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none'%3E%3Cpath d='M7 10L12 15L17 10' stroke='%23047a93' stroke-width='2.5' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E");
          box-shadow: 0 2px 4px rgba(5, 155, 182, 0.1);
        }
        .custom-dropdown:focus {
          outline: none;
          border-color: #059BB6;
          box-shadow: 0 0 0 0.2rem rgba(5, 155, 182, 0.25);
          background-image: url("data:image/svg+xml;charset=utf-8,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none'%3E%3Cpath d='M7 10L12 15L17 10' stroke='%23047a93' stroke-width='2.5' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E");
        }
        .custom-dropdown:active {
          background-image: url("data:image/svg+xml;charset=utf-8,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none'%3E%3Cpath d='M7 10L12 15L17 10' stroke='%23036b7a' stroke-width='3' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E");
        }
      `}</style>
    </div>
  )
}

export {Step1}
